import React, { useState } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { isEqual } from "lodash";

import { actions as modelActions } from "features/Model/_ducks";
import { actions as resultActions } from "features/Model/Review/Results/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { useStateSelector } from "utils/field";
import { replaceModelWithReferenceHouse } from "utils/h2kinout/api";
import { SELECTABLE_REF_HOUSE_CASES, getSb12PackageOptions, isSelectableRefHouseCase } from "utils/referenceHouse";

import Accordion from "components/Accordion";
import Button from "components/Button";
import Input from "components/Input";
import Select from "components/Input/Select";

import classes from "../style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import House from "assets/images/icons/JSX/House";

const { setModelServerSyncing, syncModelDataFromServer, updateModalDetails } = modelActions;
const { resetResults } = resultActions;
const { setUpgradePackages, setInitUpgradePackages } = upgradeActions;

const ReplaceWithReferenceHouse = ({ onReplaced }) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const modelId = useSelector((state) => state.model?.modelId) || "";
    const modelName = useSelector((state) => state.model?.modelDetails?.name) || "";
    const ownerUid = useSelector((state) => state.model?.modelDetails?.owner?.uid) || "";
    const simulationStatus = useSelector((state) => state.model?.modelDetails?.simulationStatus) || "IDLE";
    const isSimulationRunning = useSelector((state) => state.model?.modelDetails?.isSimulationRunning) || false;
    const resultsLoading = useSelector((state) => state.results?.loading) || false;
    const modelData = useSelector((state) => state.model?.modelData) || {};
    const savedModelData = useSelector((state) => state.model?.modelDataToCompare) || {};
    const upgradePackages = useSelector((state) => state.upgrades?.packages) || {};
    const savedUpgradePackages = useSelector((state) => state.upgrades?.initPackages) || {};
    const uid = useStateSelector("user.uid") || "";

    const [referenceCase, setReferenceCase] = useState("");
    const [sb12PackageId, setSb12PackageId] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [error, setError] = useState("");
    const [isReplacing, setIsReplacing] = useState(false);

    const isOwner = Boolean(uid) && uid === ownerUid;
    const simulationActive = simulationStatus === "RUNNING" || Boolean(isSimulationRunning);
    const hasUnsavedChanges = !isEqual(modelData, savedModelData) || !isEqual(upgradePackages, savedUpgradePackages);
    const sb12PackageOptions = getSb12PackageOptions(modelData);
    const sb12PackageStillValid = sb12PackageOptions.some(({ value }) => value === sb12PackageId);
    const trimmedConfirmation = confirmation.trim();
    const confirmationMatches = trimmedConfirmation === "REPLACE" || trimmedConfirmation === modelName;

    const blockingReasons = [
        simulationActive && "Wait for the current simulation to finish.",
        !simulationActive && resultsLoading && "Wait for results to finish loading.",
        hasUnsavedChanges &&
            "Save or discard your unsaved changes. Replacement is built from the last saved version of the model.",
        referenceCase &&
            !isSelectableRefHouseCase(referenceCase) &&
            "That reference house is not available yet.",
        !referenceCase && "Select a reference house.",
        referenceCase === "ontarioSb12" &&
            !sb12PackageStillValid &&
            "Select an SB-12 package that is valid for this model's heating degree days.",
        !confirmationMatches && `Type REPLACE${modelName ? ` or "${modelName}"` : ""} in the confirmation field.`,
    ].filter(Boolean);

    const replaceDisabled = isReplacing || blockingReasons.length > 0;

    const handleReplace = async () => {
        if (replaceDisabled) return;

        setIsReplacing(true);
        setError("");
        dispatch(setModelServerSyncing(true));

        try {
            const replaced = await replaceModelWithReferenceHouse({
                modelId,
                referenceHouse: {
                    refCase: referenceCase,
                    packageId: referenceCase === "ontarioSb12" ? sb12PackageId : "",
                    useHeatPumpReference: false,
                },
                confirmation: trimmedConfirmation,
            });

            batch(() => {
                dispatch(
                    updateModalDetails({
                        ...(replaced.modelDetails || {}),
                        lastCliRun: {},
                        isSimulationRunning: false,
                        simulationStatus: "IDLE",
                    })
                );
                dispatch(resetResults());
                dispatch(setUpgradePackages({ packages: {} }));
                dispatch(setInitUpgradePackages({ packages: {} }));
            });
            await dispatch(syncModelDataFromServer(modelId, { modelDetails: replaced.modelDetails }));
            dispatch(setModelServerSyncing(false));
            setReferenceCase("");
            setSb12PackageId("");
            setConfirmation("");
            setIsReplacing(false);
            onReplaced?.();
            history.push(`/models/${modelId}`);
        } catch (err) {
            dispatch(setModelServerSyncing(false));
            setError(err?.message || "Could not replace the model with a reference house.");
            setIsReplacing(false);
        }
    };

    const heading = (
        <div className={sharedClasses.accordionIconHeading}>
            Replace with Reference House <House color="#0049c6" width="16px" height="16px" />
        </div>
    );

    if (!isOwner) {
        return (
            <Accordion className={sharedClasses.accordion} heading={heading} large>
                <div className={classes.simulationSetting}>
                    <p>Only the model owner can replace this file with a reference house.</p>
                </div>
            </Accordion>
        );
    }

    return (
        <Accordion className={sharedClasses.accordion} heading={heading} large>
            <div className={classes.simulationSetting}>
                <p>
                    This replaces the saved model with the selected reference house and sets it to General mode.
                    Geometry, envelope, mechanical systems, and operating inputs may change. Every upgrade package will
                    be deleted, and current simulation results will be detached. No automatic copy is created. Duplicate
                    the model first if you need to keep this file. Read how reference houses are built in the{" "}
                    <a href="/documentation/reference-houses" target="_blank" rel="noopener noreferrer">
                        documentation
                    </a>
                    .
                </p>
                <div className={classes.field}>
                    <Select
                        name="replaceReferenceHouseCase"
                        label="Reference house"
                        placeholder="Select reference house"
                        search
                        options={SELECTABLE_REF_HOUSE_CASES}
                        disabled={isReplacing}
                        input={{
                            value: referenceCase,
                        onChange: (value) => {
                            if (!isSelectableRefHouseCase(value)) return;
                            setReferenceCase(value);
                                setError("");
                                if (value !== "ontarioSb12") {
                                    setSb12PackageId("");
                                }
                            },
                        }}
                    />
                </div>
                {referenceCase === "ontarioSb12" && (
                    <div className={classes.field}>
                        <Select
                            name="replaceSb12Package"
                            label="SB-12 package"
                            placeholder="Select SB-12 package"
                            options={sb12PackageOptions}
                            disabled={isReplacing}
                            input={{
                                value: sb12PackageStillValid ? sb12PackageId : "",
                                onChange: (value) => {
                                    setSb12PackageId(value);
                                    setError("");
                                },
                            }}
                        />
                    </div>
                )}
            <div className={classes.field}>
                <Input
                        type="text"
                        name="replaceConfirmation"
                        label="Type REPLACE or the model name"
                        placeholder={modelName ? `REPLACE or ${modelName}` : "REPLACE"}
                        disabled={isReplacing}
                        input={{
                            value: confirmation,
                            onChange: (value) => {
                                setConfirmation(value);
                                setError("");
                            },
                        }}
                    />
                </div>
                {!isReplacing && blockingReasons.length > 0 && (
                    <div className={classes.replaceBlockers}>
                        <strong>Before you can replace this model:</strong>
                        <ul>
                            {blockingReasons.map((reason) => (
                                <li key={reason}>{reason}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {error && <p className={classes.replaceError}>{error}</p>}
                <Button
                    className={classes.replaceButton}
                    type="red"
                    onClick={handleReplace}
                    disabled={replaceDisabled}
                    loading={isReplacing}
                >
                    Replace model
                </Button>
            </div>
        </Accordion>
    );
};

export default ReplaceWithReferenceHouse;
